// /oversight/records?project=<id>&from&to&user&sort&dir&page&size (architecture sprint-11 §9.3). A
// Supervisor's read-only Records for one project they review: the Admin table, filters (project fixed by
// the picker) and pagination, fed by /api/oversight/projects/:id/records. Exports only with exportTeam.
import { Download } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EmptyState } from '@/components/EmptyState';
import { Page } from '@/components/Page';
import { PageHeader } from '@/components/PageHeader';
import { ProjectScopeSelect, resolveScope } from '@/components/ProjectScopeSelect';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useMe } from '@/hooks/use-me';
import { useOversightPeople, useOversightProjects, useOversightRecords, useProjectRecordsExport } from '@/hooks/use-oversight';
import { formatHours2 } from '@/lib/format';
import { defaultRecordsParams, nextSort, pageCount, parseRecordsParams, recordsSearch, type RecordsParams } from '@/lib/records-model';
import { useToday } from '@/lib/use-now';
import { RecordsFilters } from '@/pages/admin/records/RecordsFilters';
import { RecordsPagination } from '@/pages/admin/records/RecordsPagination';
import { RecordsTable } from '@/pages/admin/records/RecordsTable';
import type { RecordSort } from '@/types/admin';
import type { OversightProject } from '@/types/settings';

type ExportFormat = 'csv' | 'xlsx';

export function OversightRecordsPage(): JSX.Element {
  const { data: me } = useMe();
  const projects = useOversightProjects();
  if (!me || !projects.data)
    return (
      <Page width="6xl">
        <Skeleton className="h-96 w-full" />
      </Page>
    );
  const choices = projects.data.filter((p) => p.caps.viewTeam);
  if (choices.length === 0)
    return (
      <Page width="6xl">
        <PageHeader title="Records" />
        <EmptyState title="No projects to review." />
      </Page>
    );
  return <Records tz={me.timezone} choices={choices} />;
}

function Records({ tz, choices }: { tz: string; choices: OversightProject[] }): JSX.Element {
  const today = useToday(tz);
  const [search, setSearch] = useSearchParams();
  const { scope } = resolveScope(search.get('project'), choices, false);
  const project = choices.find((p) => p.id === scope) ?? choices[0];
  const projectId = project?.id ?? null;
  const people = useOversightPeople(projectId);

  const known = useMemo(() => ({ userIds: people.data ? new Set(people.data.map((u) => u.id)) : undefined }), [people.data]);
  const parsed = parseRecordsParams(search, today, known);
  const params: RecordsParams = { ...parsed.params, project: projectId ?? 'all' };
  const canonical = recordsSearch(params).toString() === search.toString();

  useEffect(() => {
    if (!canonical) setSearch(recordsSearch(params), { replace: true });
  }, [canonical, params, setSearch]);

  const records = useOversightRecords(projectId, params);
  const pages = records.data ? pageCount(records.data.total, params.size) : 1;
  useEffect(() => {
    if (records.data && !records.isPlaceholderData && params.page > pages) setSearch(recordsSearch({ ...params, page: pages }), { replace: true });
  }, [records.data, records.isPlaceholderData, params, pages, setSearch]);

  const update = (next: Partial<RecordsParams>, resetPage = true): void => {
    setSearch(recordsSearch({ ...params, ...next, ...(resetPage ? { page: 1 } : {}) }));
  };

  const exporter = useProjectRecordsExport();
  const [inFlight, setInFlight] = useState<ExportFormat | null>(null);
  function runExport(format: ExportFormat): void {
    if (!projectId) return;
    setInFlight(format);
    exporter.mutate({ projectId, params, format }, { onSettled: () => setInFlight(null) });
  }

  return (
    <Page width="6xl">
      <PageHeader
        title="Records"
        hint="Entries on the project you review, read-only. Filter, sort and export what you see."
        actions={
          project?.caps.exportTeam
            ? (['csv', 'xlsx'] as const).map((format) => (
                <Button key={format} variant="outline" size="sm" onClick={() => runExport(format)} disabled={inFlight === format}>
                  <Download /> {inFlight === format ? 'Exporting…' : `Export ${format.toUpperCase()}`}
                </Button>
              ))
            : undefined
        }
      />
      <div className="mb-4">
        <ProjectScopeSelect id="oversight-records-project" value={projectId ?? ''} projects={choices} allowAll={false} onChange={(p) => update({ project: p, user: 'all' })} />
      </div>
      <RecordsFilters
        from={params.from}
        to={params.to}
        user={params.user}
        project={params.project}
        users={people.data ?? []}
        activeProjects={[]}
        archivedProjects={[]}
        hideProject
        onRangeChange={(r) => update(r)}
        onResetRange={() => {
          const d = defaultRecordsParams(today);
          update({ from: d.from, to: d.to });
        }}
        onUserChange={(user) => update({ user })}
        onProjectChange={() => undefined}
      />
      <p className="mb-3 text-sm text-muted-foreground" aria-live="polite">
        {records.data ? `${records.data.total} entries · ${formatHours2(records.data.totalSeconds)} h` : '…'}
      </p>
      {!records.data ? (
        <Skeleton className="h-72 w-full rounded-xl" />
      ) : records.data.items.length === 0 ? (
        <EmptyState title="No entries match these filters." />
      ) : (
        <RecordsTable rows={records.data.items} sort={params.sort} dir={params.dir} onSort={(column: RecordSort) => update(nextSort(params, column))} />
      )}
      <RecordsPagination page={params.page} pages={pages} size={params.size} onPage={(page) => update({ page }, false)} onSize={(size) => update({ size })} />
    </Page>
  );
}
